import { useState, useRef, useEffect, useCallback } from 'react'; // refreshed
import { CheckCircle, Loader2, Play, Pause, Volume2, VolumeX, Video, CircleAlert, AlertTriangle, ChevronLeft } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import type { StaffVideoTask } from '@/services/data/staffVideoContent';
import { advanceVEStatus } from '@/services/data/veActions';
import { getS3DownloadUrl } from '@/services/data/s3Files';
import { VideoCommentsDrawer } from '@/components/modals/VideoCommentsDrawer';
import { TimelineComment } from '@/types/video-validation';
import { getVideoCommentsWithBatches, mapDbCommentToTimelineComment, linkCommentsToBatch } from '@/services/data/videoComments';
import { createCommentBatch } from '@/services/data/commentBatches';
import { createFeedbackPurchase } from '@/services/data/feedbackPurchases';
import { notifyFeedbackWebhook } from '@/services/data/feedbackWebhook';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const VE_VALIDATE_DISMISS_KEY = 've_validate_dismiss';

interface VEReviewDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly task: StaffVideoTask;
  readonly onDone?: () => void;
}

export function VEReviewDialog({ open, onOpenChange, task, onDone }: Readonly<VEReviewDialogProps>) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { userId } = useAuthStore();

  const [isValidating, setIsValidating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [comments, setComments] = useState<TimelineComment[]>([]);
  const [activeVersion, setActiveVersion] = useState(0);
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});
  const [showValidateConfirm, setShowValidateConfirm] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);

  const isMultihook = task.is_multihook;

  const rawVideoKeys = isMultihook
    ? [task.video_1_url, task.video_2_url, task.video_3_url].filter(Boolean)
    : [task.video_1_url].filter(Boolean);

  const activeVideoUrl = resolvedUrls[rawVideoKeys[activeVersion]] || resolvedUrls[rawVideoKeys[0]] || null;

  // Resolve S3 keys to presigned URLs
  useEffect(() => {
    if (!open || rawVideoKeys.length === 0) return;
    let cancelled = false;
    const resolve = async () => {
      const entries: Record<string, string> = {};
      await Promise.all(
        rawVideoKeys.map(async (key) => {
          if (key.startsWith('http')) {
            entries[key] = key;
          } else {
            const result = await getS3DownloadUrl(key, false);
            if (result.success && result.data?.url) {
              entries[key] = result.data.url;
            }
          }
        })
      );
      if (!cancelled) setResolvedUrls(entries);
    };
    resolve();
    return () => { cancelled = true; };
  }, [open, task.id]);

  // Load comments
  useEffect(() => {
    if (!open || !task.id) return;
    getVideoCommentsWithBatches(task.id, activeVersion + 1).then(result => {
      if (result.success && result.data) {
        setComments(result.data.map(mapDbCommentToTimelineComment));
      }
    });
  }, [open, task.id, activeVersion]);

  // Reset video when version changes
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
    }
  }, [activeVersion]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(console.error);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (video) video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video) setCurrentTime(video.currentTime);
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (video) setDuration(video.duration);
  };

  const handleSeekTo = (time: number) => {
    const video = videoRef.current;
    if (video) video.currentTime = time;
  };

  const handleValidateClick = () => {
    const dismissed = localStorage.getItem(VE_VALIDATE_DISMISS_KEY) === 'true';
    if (dismissed) {
      handleValidate();
    } else {
      setShowValidateConfirm(true);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    const result = await advanceVEStatus(task.id);
    if (result.success) {
      toast.success('Contenu validé → envoyé au client');
      onDone?.();
    } else {
      toast.error(result.error || 'Erreur');
    }
    setIsValidating(false);
  };

  const handleConfirmValidate = () => {
    if (dontShowAgain) {
      localStorage.setItem(VE_VALIDATE_DISMISS_KEY, 'true');
    }
    setShowValidateConfirm(false);
    handleValidate();
  };

  const handleCommentsChange = useCallback((updatedComments: TimelineComment[]) => {
    setComments(updatedComments);
  }, []);

  // Helper to check if a comment is unsent
  const isUnsentComment = (comment: TimelineComment): boolean => {
    const hasBatch = comment.batchId !== undefined && comment.batchId !== null;
    const markedAsSent = comment.isSent === true;
    return !hasBatch && !markedAsSent;
  };

  const handleSendFeedback = async () => {
    if (!userId || isSendingFeedback) return;

    const unsentComments = comments.filter(c => isUnsentComment(c) && c.dbId);
    if (unsentComments.length === 0) return;

    const hasSentBatch = comments.some(c => c.batchId !== undefined && c.batchId !== null);

    setIsSendingFeedback(true);
    try {
      // Create batch
      const batchResult = await createCommentBatch({
        video_content_id: task.id,
        user_id: userId,
      });
      if (!batchResult.success || !batchResult.data) throw new Error('Failed to create batch');
      const batchId = batchResult.data.id;

      // Link comments to batch
      const commentDbIds = unsentComments
        .map(c => c.dbId)
        .filter((id): id is string => !!id);
      await linkCommentsToBatch(commentDbIds, batchId);

      // Notify n8n webhook (fire & forget)
      notifyFeedbackWebhook(batchId);

      // If second batch, create purchase record
      if (hasSentBatch) {
        await createFeedbackPurchase({
          user_id: userId,
          video_content_id: task.id,
          video_comment_batch_id: batchId,
          amount_cents: 5000,
          status: 'pending',
          notes: `Batch de retours complémentaires - ${unsentComments.length} commentaire(s)`
        });
      }

      // Update local state
      const commentIds = new Set(commentDbIds);
      setComments(prev => prev.map(c => {
        if (c.dbId && commentIds.has(c.dbId)) {
          return { ...c, isSent: true, batchId };
        }
        return c;
      }));

      toast.success('Retours envoyés avec succès');
    } catch (error) {
      console.error("Error sending feedback:", error);
      toast.error("Erreur lors de l'envoi des retours");
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] max-h-[100vh] w-screen h-screen bg-[rgba(10,10,10,0.95)] backdrop-blur-lg border-none p-0 gap-0 overflow-hidden [&>button]:text-white/60 [&>button]:hover:text-white">
        {/* Top header bar */}
        <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between">
          <button
            onClick={() => onOpenChange(false)}
            className="flex items-center gap-2 text-white/60 hover:text-white/80 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="text-sm font-medium">Retour</span>
          </button>
          <button
            onClick={handleValidateClick}
            disabled={isValidating}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm font-semibold hover:bg-emerald-500/25 hover:border-emerald-500/40 transition-all disabled:opacity-50"
          >
            {isValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            Valider ce contenu
          </button>
        </div>

        <div className="flex items-center justify-center w-full h-full gap-8">
          {/* BLOC 1: Left panel — Feedback */}
          <div className="w-[324px] h-[576px] flex-shrink-0 flex flex-col rounded-3xl overflow-hidden p-2" style={{ background: '#141414', border: '1px solid #262626' }}>
            {/* Title */}
            <div className="p-4 space-y-3 flex-shrink-0 border-b border-white/10">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-white leading-tight flex-1 line-clamp-2">
                  {task.title || 'Vidéo sans titre'}
                </h2>
                {isMultihook && (
                  <Badge variant="outline" className="flex-shrink-0 border-purple-500/30 text-purple-400 text-[10px]">
                    <Video className="h-3 w-3 mr-1" />
                    A/B
                  </Badge>
                )}
              </div>
            </div>

            {/* Embedded feedback */}
            <div className="flex-1 overflow-hidden relative">
              <VideoCommentsDrawer
                videoId={task.id}
                videoVersion={activeVersion + 1}
                currentTime={currentTime}
                duration={duration}
                initialComments={comments}
                isPlaying={isPlaying}
                currentUserName="VE"
                initialPosition="full"
                embedded
                isStaffMode
                onClose={() => {}}
                onSeekTo={handleSeekTo}
                onTogglePlay={togglePlay}
                onCommentsChange={handleCommentsChange}
                onSendFeedback={handleSendFeedback}
                isSendingFeedback={isSendingFeedback}
              />
            </div>
          </div>

          {/* BLOC 2: Video Player (324×576) */}
          <div className="flex-shrink-0">
            <div
              className="relative w-[324px] h-[576px] rounded-3xl overflow-hidden cursor-pointer"
              style={{ boxShadow: '0 0 0 2px hsla(49, 100%, 52%, 0.5)' }}
            >
              {/* Status badge */}
              <div className="absolute top-3 right-3 z-10">
                <span
                  className="inline-flex items-center rounded-full text-[10px] font-medium uppercase tracking-[0.05em] px-2 py-1 gap-1.5"
                  style={{
                    background: 'rgba(255, 207, 12, 0.1)',
                    border: '1px solid rgba(255, 207, 12, 0.3)',
                    color: '#FFCF0C',
                  }}
                >
                  <CircleAlert className="w-3 h-3" />
                  À VALIDER
                </span>
              </div>

              {/* Video */}
              {activeVideoUrl ? (
                <button
                  className="w-full h-full relative cursor-pointer p-0 border-none bg-transparent"
                  onClick={togglePlay}
                  aria-label="Toggle video playback"
                >
                  <video
                    ref={videoRef}
                    src={`${activeVideoUrl}#t=0.001`}
                    className="w-full h-full object-cover"
                    muted={isMuted}
                    playsInline
                    preload="auto"
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  >
                    <track kind="captions" srcLang="fr" label="Français" default />
                  </video>

                  {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                      <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Play className="h-6 w-6 text-white ml-1" />
                      </div>
                    </div>
                  )}

                  <button
                    onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                    className="absolute top-3 left-3 z-20 bg-black/50 backdrop-blur-sm rounded-full p-2 text-white hover:bg-black/70 transition-all"
                  >
                    {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>

                  {/* Progress bar with comment markers */}
                  <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/80 to-transparent" onClick={(e) => e.stopPropagation()}>
                    {/* Comment markers */}
                    <div className="relative h-5 mb-1.5 mx-8">
                      {comments.filter(c => !c.parentId).map((comment, index) => {
                        const position = duration > 0 ? (comment.timestamp / duration) * 100 : 0;
                        return (
                          <button
                            key={`${comment.id}-${index}`}
                            className="absolute -translate-x-1/2 bottom-0 cursor-pointer p-0 border-none bg-transparent"
                            style={{ left: `${position}%` }}
                            onClick={() => handleSeekTo(comment.timestamp)}
                            aria-label={`Jump to comment at ${formatTime(comment.timestamp)}`}
                          >
                            <div className={cn(
                              "w-3 h-3 rounded-full shadow-lg border border-black/30",
                              comment.isSent ? "bg-amber-400" : "bg-white/40"
                            )} />
                          </button>
                        );
                      })}
                    </div>
                    {/* Playbar controls */}
                    <div className="flex items-center gap-3">
                      <button onClick={togglePlay} className="text-white flex-shrink-0">
                        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 fill-white" />}
                      </button>
                      <input
                        type="range"
                        className="flex-1 h-1 bg-white/20 rounded-full cursor-pointer appearance-none accent-white"
                        min={0}
                        max={duration || 0}
                        value={currentTime}
                        onChange={(e) => {
                          const video = videoRef.current;
                          if (video) video.currentTime = Number.parseFloat(e.currentTarget.value);
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.focus();
                        }}
                        aria-label="Video progress"
                      />
                      <span className="text-xs text-white/50 font-mono tabular-nums">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                    </div>
                  </div>
                </button>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#1f1f1f] text-white/30 text-sm">
                  Aucune vidéo disponible
                </div>
              )}
            </div>
          </div>

          {/* BLOC 3: Thumbnails A/B/C (conditional) */}
          {isMultihook && rawVideoKeys.length > 1 && (
            <div className="flex-shrink-0 flex flex-col gap-3 items-center">
              {rawVideoKeys.map((key, i) => {
                const isActive = activeVersion === i;
                const thumbUrl = resolvedUrls[key];
                return (
                  <button
                    key={i}
                    onClick={() => setActiveVersion(i)}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <span
                      className={cn(
                        "text-[10px] font-semibold uppercase tracking-[0.05em] px-2 py-0.5 rounded-full transition-colors",
                        isActive
                          ? "bg-warning text-[#1a1a00]"
                          : "bg-[#1f1f1f] text-white/60"
                      )}
                    >
                      Version {String.fromCodePoint(65 + i)}
                    </span>
                    <div
                      className={cn(
                        "w-[68px] h-[121px] rounded-xl overflow-hidden transition-all duration-200 relative",
                        isActive ? "scale-105" : "opacity-60"
                      )}
                      style={{
                        boxShadow: isActive
                          ? '0 0 0 2px #FFCF0C'
                          : '0 0 0 1px #262626',
                      }}
                    >
                      <video
                        src={thumbUrl ? `${thumbUrl}#t=0.5` : undefined}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                        playsInline
                      >
                        <track kind="captions" srcLang="fr" label="Français" default />
                      </video>
                      {isActive && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(255,207,12,0.1)' }}>
                          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,207,12,0.9)' }}>
                            <Play className="w-3 h-3 fill-[#1a1a00] text-[#1a1a00]" />
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Validate confirmation dialog */}
    <AlertDialog open={showValidateConfirm} onOpenChange={setShowValidateConfirm}>
      <AlertDialogContent className="bg-black/95 border-white/10 text-white">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-emerald-500/20">
              <AlertTriangle className="h-5 w-5 text-emerald-400" />
            </div>
            <AlertDialogTitle>Rendre visible au client</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-white/70 space-y-3">
            <p>
              En validant ce contenu, il deviendra <strong className="text-white">visible par le client</strong> pour sa review.
            </p>
            <p className="text-white/50 text-xs">
              Assurez-vous que le montage est prêt avant de continuer.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex items-center gap-2 px-1">
          <Checkbox
            id="dont-show-again"
            checked={dontShowAgain}
            onCheckedChange={(checked) => setDontShowAgain(!!checked)}
            className="border-white/30 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
          />
          <label htmlFor="dont-show-again" className="text-xs text-white/50 cursor-pointer select-none">
            Ne plus afficher ce message
          </label>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/10 border-white/10 text-white/60 hover:bg-white/20 hover:text-white/80">
            Annuler
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleConfirmValidate(); }}
            className="bg-emerald-500 text-black hover:bg-emerald-400"
          >
            Valider et envoyer au client
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
}
